import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { sendOtp } from "../../services/sendOtpService";
import { verifyOtp } from "../../services/verifyOtpService";
import Loader from "../../components/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { getProfile } from "../../services/userService";
import { useTheme } from "../../context/ThemeContext";
import secureStorage from "../../utils/secureStorage";

function OTPVerification() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPasswordReset = searchParams.get("type") === "reset";

  // Keep current UI (4 inputs) but upgrade logic
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [email, setEmail] = useState("");

  // Initialize timers from secureStorage, fallback to defaults
  const [otpTimer, setOtpTimer] = useState(() => {
    const saved = secureStorage.getItem("otp_expiration");
    if (saved) {
      const expirationTime = parseInt(saved, 10);
      const secondsLeft = Math.max(
        0,
        Math.floor((expirationTime - Date.now()) / 1000)
      );
      return secondsLeft;
    }
    return 60;
  });

  const [resendTimer, setResendTimer] = useState(() => {
    const saved = secureStorage.getItem("resend_expiration");
    if (saved) {
      const expirationTime = parseInt(saved, 10);
      const secondsLeft = Math.max(
        0,
        Math.floor((expirationTime - Date.now()) / 1000)
      );
      return secondsLeft;
    }
    return 0; // allow immediate resend by default
  });

  // Load email and seed expiration timestamps on mount
  useEffect(() => {
    if (isPasswordReset) {
      const resetEmail = secureStorage.getItem("reset_email");
      setEmail(resetEmail || "");
    } else {
      const storedUser = secureStorage.getJSON("user");
      if (storedUser) {
        setEmail(storedUser?.email || "");
      }
    }
    if (!secureStorage.getItem("otp_expiration")) {
      secureStorage.setItem("otp_expiration", String(Date.now() + 60 * 1000));
    }
    if (!secureStorage.getItem("resend_expiration")) {
      secureStorage.setItem("resend_expiration", "0");
    }
  }, [isPasswordReset]);

  // Single ticking interval to keep timers in sync across reloads/tabs
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();

      const otpExp = parseInt(
        secureStorage.getItem("otp_expiration") || "0",
        10
      );
      setOtpTimer(Math.max(0, Math.floor((otpExp - now) / 1000)));

      const resendExp = parseInt(
        secureStorage.getItem("resend_expiration") || "0",
        10
      );
      setResendTimer(Math.max(0, Math.floor((resendExp - now) / 1000)));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Format timer display (mm:ss)
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const handleChange = (e, idx) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length > 1) return;
    const next = [...otp];
    next[idx] = value;
    setOtp(next);
    if (value && idx < otp.length - 1) {
      const nextInput = document.getElementById(`otp-${idx + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...otp];
      if (next[idx]) {
        next[idx] = "";
        setOtp(next);
      } else if (idx > 0) {
        next[idx - 1] = "";
        setOtp(next);
        const prevEl = document.getElementById(`otp-${idx - 1}`);
        if (prevEl) prevEl.focus();
      }
    } else if (e.key === "Delete") {
      e.preventDefault();
      const next = [...otp];
      next[idx] = "";
      setOtp(next);
    } else if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/[^0-9]/g, "").slice(0, otp.length);
        const next = [...otp];
        for (let i = 0; i < otp.length; i++) next[i] = digits[i] || "";
        setOtp(next);
        const lastFilledIndex = Math.min(digits.length - 1, otp.length - 1);
        if (lastFilledIndex >= 0) {
          const el = document.getElementById(`otp-${lastFilledIndex}`);
          if (el) el.focus();
        }
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let token = secureStorage.getItem("email_token") || "";
      if (!token && !isPasswordReset) {
        const storedUser = secureStorage.getJSON("user") || {};
        token = storedUser.email_token || "";
      }

      const otpValue = otp.join("");
      const resData = await verifyOtp(token, otpValue);

      if (resData.success) {
        // Clear timers on success
        secureStorage.removeItem("otp_expiration");
        secureStorage.removeItem("resend_expiration");

        if (isPasswordReset) {
          navigate("/reset-password");
        } else {
          const profileData = await getProfile();
          if (profileData.success) {
            secureStorage.setJSON("user", profileData.data);
            navigate("/verification");
          } else {
            const msg =
              profileData.message ||
              t("failed_to_fetch_profile", "Failed to fetch profile");
            setError(msg);
            toast.error(msg);
          }
        }
      } else {
        const msg = resData.message || t("invalid_otp", "Invalid OTP");
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      // Optional: surface a generic error
      // toast.error(t("failed_to_verify_otp", "Failed to verify OTP"));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email || resendTimer > 0) return;
    setResendLoading(true);
    setError("");

    // Use different otp_purpose based on the flow
    const otpPurpose = isPasswordReset ? 1 : 2;
    const resData = await sendOtp(email, otpPurpose);

    if (resData.success) {
      const now = Date.now();
      const resendSecs = resData.data?.email_resend_time || 60;
      // Reset UI timers
      setOtpTimer(60);
      setResendTimer(resendSecs);
      // Persist timer expirations so they survive reloads/tabs
      secureStorage.setItem("otp_expiration", String(now + 60 * 1000));
      secureStorage.setItem(
        "resend_expiration",
        String(now + resendSecs * 1000)
      );
      // Store the fresh email token consistently where we read it from
      if (resData.data?.email_token) {
        secureStorage.setItem("email_token", resData.data.email_token);
      }
      // toast.success(resData.message || "OTP resent successfully!");
    } else {
      setError(resData.message || "Failed to resend OTP");
      toast.error(resData.message || "Failed to resend OTP");
    }
    setResendLoading(false);
  };

  const handleBack = () => {
    if (isPasswordReset) {
      navigate("/forgot-password");
    } else {
      navigate("/register");
    }
  };

  return (
    <div className="flex min-h-screen px-4 sm:px-6 md:px-10 bg-white dark:bg-[#0A0C1C]  items-center justify-center">
      <div className="flex flex-col md:flex-row w-full pt-10">
        <div className="flex flex-col w-full items-center justify-center pb-5">
          <div 
            className="loginWrapper text-white w-full sm:w-11/12 md:w-2/4 lg:w-2/5 xl:w-2/5 rounded-xl px-5 sm:px-10 py-6 shadow-lg"
            style={{ backgroundColor: isDarkMode ? "#0A0C1C" : "#6D29F6" }}
          >
            {/* Back Button */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={handleBack}
                className="flex items-center text-white hover:text-gray-300 transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                {t("back")}
              </button>

              <div></div>
            </div>
            <div className="text-center  flex flex-col gap-2 justify-center items-center">
              <img src="/logo-bg.png" alt="VibeLinks Logo" className="h-16" />
              <h1 className="text-2xl font-bold">
                {t("app_name", "VibeLinks")}
              </h1>
            </div>
            {/* Logo */}
            <div className="text-center py-6"></div>
            <form onSubmit={handleSubmit} className="w-full">
              <div className="mb-6 text-center">
                <h2 className="text-xl font-semibold mb-2 text-white">
                  {isPasswordReset
                    ? t("password_reset_verification")
                    : t("otp_verification")}
                </h2>
                <p className="text-sm mb-4 text-white">
                  {t("verification_code_sent_to", { email })}
                </p>
                {/* OTP Timer Display */}
                <div className="mb-4">
                  <p className="text-sm text-white">
                    {t("otp_expires_in")}:{" "}
                    <span className="font-semibold text-white">
                      {formatTime(otpTimer)}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex justify-center mb-4 gap-2">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    id={`otp-${idx}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(e, idx)}
                    onKeyDown={(e) => handleKeyDown(e, idx)}
                    className={`w-12 h-12 text-center text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6d29f6] ${
                      otpTimer === 0
                        ? "border-purple-500 opacity-50"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                    style={isDarkMode ? { backgroundColor: '#ffffff', color: '#000000' } : { backgroundColor: '#ffffff', color: '#000000' }}
                    disabled={otpTimer === 0}
                  />
                ))}
              </div>

              {/* {error && (
                <p className='text-purple-500 text-xs text-center mb-2'>
                  {error}
                </p>
              )} */}
              <div className="text-center mb-4 flex justify-center items-center gap-3">
                <span className="text-xs text-white">{t("no_code_yet")} </span>

                <button
                  type="button"
                  className="text-xs underline flex items-center justify-center text-white"
                  onClick={handleResend}
                  disabled={resendLoading || resendTimer > 0}
                >
                  {resendLoading ? (
                    <>
                      <Loader loaderColor="[#6d29f6]" />
                      <span className="ml-1">{t("sending")}...</span>
                    </>
                  ) : resendTimer > 0 ? (
                    `${t("resend_code")} `
                  ) : (
                    t("resend_code")
                  )}
                </button>
              </div>
              <button
                type="submit"
                disabled={loading || otpTimer === 0}
                className={`w-full py-2 rounded-lg font-semibold transition duration-150 flex items-center justify-center ${
                  otpTimer === 0
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : ""
                }`}
                style={
                  otpTimer === 0
                    ? {}
                    : isDarkMode
                    ? { backgroundColor: '#6d29f6', color: '#ffffff' }
                    : { backgroundColor: '#ffffff', color: '#6d29f6' }
                }
              >
                {loading ? (
                  <>
                    <Loader loaderColor="[#6d29f6]" />
                    <span className="ml-2">{t("verifying")}</span>
                  </>
                ) : otpTimer === 0 ? (
                  t("otp_expired")
                ) : (
                  t("submit")
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OTPVerification;
